import { createContext, useContext } from "react";
import { cn } from "lib/utils";

/**
 * Composable, Lovable-style data grid (div-based, NOT a <table>).
 *
 * Mirrors the box-flow-delight list pattern: a CSS-grid header row + grid data
 * rows that share one column template, with subtle border separators. Replaces
 * the legacy <table>/<thead>/<tbody> markup across the Settings surfaces.
 *
 * Usage:
 *   <DataGrid columns="minmax(0,2fr) minmax(0,3fr) 1fr auto" minWidth={560}>
 *     <DataGridHeader>
 *       <DataGridCell>Name</DataGridCell>
 *       <DataGridCell>Email</DataGridCell>
 *       <DataGridCell>Role</DataGridCell>
 *       <DataGridCell className="text-right">Actions</DataGridCell>
 *     </DataGridHeader>
 *     {rows.map((r) => (
 *       <DataGridRow key={r.id}>
 *         <DataGridCell>{r.name}</DataGridCell>
 *         ...
 *       </DataGridRow>
 *     ))}
 *   </DataGrid>
 *
 * `columns` is any CSS grid-template-columns value (shared by header + rows).
 * `minWidth` (number px) keeps columns readable on small screens — the grid
 * scrolls horizontally inside its own container rather than squashing.
 */
const DataGridContext = createContext({ columns: "" });

export function DataGrid({ columns, minWidth, className, children, ...props }) {
  return (
    <DataGridContext.Provider value={{ columns }}>
      <div className={cn("w-full overflow-x-auto", className)} role="table" {...props}>
        <div style={minWidth ? { minWidth } : undefined}>{children}</div>
      </div>
    </DataGridContext.Provider>
  );
}

export function DataGridHeader({ className, children, ...props }) {
  const { columns } = useContext(DataGridContext);
  return (
    <div
      role="row"
      style={{ gridTemplateColumns: columns }}
      className={cn(
        "grid items-center gap-6 border-b border-border pb-3 text-sm font-semibold text-foreground",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function DataGridRow({ className, children, ...props }) {
  const { columns } = useContext(DataGridContext);
  return (
    <div
      role="row"
      style={{ gridTemplateColumns: columns }}
      className={cn(
        "grid items-center gap-6 border-b border-border py-3 text-sm text-foreground transition-colors hover:bg-muted/50 last:border-b-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function DataGridCell({ className, children, ...props }) {
  return (
    <div role="cell" className={cn("min-w-0 truncate", className)} {...props}>
      {children}
    </div>
  );
}
